import ChevronIcon from "$/lib/icons/ChevronIcon";
import { dateTimeFormatter } from "$/lib/utils/constants/intl.constants";
import { cn } from "$/lib/utils/functions/misc.functions";
import { CalendarContainer } from "react-datepicker";
import Flexbox from "../../common/Flexbox";
import { DatePickerInput } from "../DatePickerInput";
import { Tabs } from "../Tabs";
import type { DateRangePickerInputProps } from "./DateRangePickerInput.types";
import { Button } from "../../atoms";
import { useEffect, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

const DateRangePickerInput = ({
  startDate,
  endDate,
  className,
  onChange,
  tabs,
  theme = "blue",
  ...datePickerInputProps
}: DateRangePickerInputProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);

  const formattedStartDate = startDate
    ? dateTimeFormatter.format(new Date(startDate))
    : "Début";
  const formattedEndDate = endDate
    ? dateTimeFormatter.format(new Date(endDate))
    : "Fin";

  useOnClickOutside(ref, () => {
    if (isOpen) setIsOpen(false);
  });

  useEffect(() => {
    setDateRange([
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ]);
  }, [startDate, endDate]);

  return (
    <Flexbox ref={ref} className="relative">
      <button
        type="button"
        className="bg-blue-light flex items-center gap-2 text-black"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span
          className={cn("whitespace-nowrap text-sm font-medium", {
            "text-primary": theme === "blue",
            "text-forest": theme === "green",
            "text-warning": theme === "orange",
          })}
        >
          {formattedStartDate} - {formattedEndDate}
        </span>
        <ChevronIcon
          className={cn("rotate-90 text-primary", {
            "text-primary": theme === "blue",
            "text-forest": theme === "green",
            "text-warning": theme === "orange",
          })}
        />
      </button>

      {isOpen && (
        <DatePickerInput
          parentContainerClassName="absolute top-6 z-50 -right-10"
          className={cn("min-h-10 rounded-lg px-4 py-2", className)}
          startDate={dateRange[0] ? new Date(dateRange[0]) : undefined}
          endDate={dateRange[1] ? new Date(dateRange[1]) : undefined}
          onChange={setDateRange}
          shouldCloseOnSelect={false}
          inline
          selectsRange
          theme={theme}
          customInput={<></>}
          calendarContainer={({ children, className }) => {
            return (
              <CalendarContainer className={cn(className)}>
                <Flexbox row fullWidth className="gap-2">
                  {tabs && (
                    <Tabs
                      type="bordered"
                      navClassName="flex-col whitespace-nowrap p-2"
                      tabs={tabs.options}
                      activeTab={tabs.selectedTabId}
                      handleTabChange={(id) => {
                        tabs.setSelectedTabId(id);
                        setIsOpen(false);
                      }}
                      theme={theme}
                    />
                  )}
                  <Flexbox className="relative mb-2 mr-2 gap-2">
                    <Flexbox row className="relative">
                      {children}
                    </Flexbox>

                    <Flexbox
                      row
                      fullWidth
                      justify="end"
                      className="-mt-3 gap-2"
                    >
                      <Button
                        className="w-fit border border-tertiary-800 bg-transparent text-tertiary-800"
                        onClick={() => {
                          setIsOpen(false);
                          setDateRange([
                            startDate ? new Date(startDate) : null,
                            endDate ? new Date(endDate) : null,
                          ]);
                        }}
                      >
                        Annuler
                      </Button>
                      <Button
                        className={cn("w-fit", {
                          "bg-primary": theme === "blue",
                          "bg-forest": theme === "green",
                          "bg-warning": theme === "orange",
                        })}
                        onClick={() => {
                          if (dateRange[1]) {
                            onChange(dateRange);
                            setIsOpen(false);
                          }
                        }}
                      >
                        Appliquer
                      </Button>
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
              </CalendarContainer>
            );
          }}
          {...datePickerInputProps}
        >
          {datePickerInputProps.children}
        </DatePickerInput>
      )}
    </Flexbox>
  );
};

DateRangePickerInput.displayName = "DateRangePickerInput";

export default DateRangePickerInput;
